import { useEffect, useState } from "react";
import {
    CustRelationChoice,
    CustSex,
    formatDateInReact,
    formatDateInSnp,
    isEmpty,
    printDebugLog,
} from "../../constants/Constants";
import {
    Button,
    FormControlLabel,
    FormLabel,
    Grid,
    IconButton,
    Paper,
    Radio,
    RadioGroup,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { SINGLE_CUST_FORM } from "../../constants/pojoservice";
import { ESTIMATED_ADULTS } from "../../constants/ConfigConstant";
import DocumentManager from "../drive/DocumentManager";

const CustomerDetailsForm = ({
    billId, paymentInitiated,
    customerInformation,
    setCustomerInformation,
    singleCustData, setSingleCustData,
    handleGetCustomerByMobile,
    setSnpComponent,
    custDelSnippet,
    deletCustomerData,
    keyMapInformation, setKeyMapInformation, spocAdded, setSpocAdded, apiToken, shopHash, disabledOperation
}) => {
    const todayFormattedDate = new Date().toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
    });


    const [isEdit, setIsEdit] = useState(false);
    const [showForm, setShowForm] = useState(true);

    useEffect(() => {
        printDebugLog("CustomerDetailsForm useEffetc", singleCustData)
    }, [singleCustData]);


    const handleAddOrUpdate = () => {
        setCustomerInformation((prev) => {
            const items = prev?.items ?? [];
            const idx = items.findIndex((x) => x.index === (singleCustData?.index ?? -1));

            let next;
            if (idx !== -1) {
                // Update existing item (immutable)
                next = items.map((x, i) => (i === idx ? { ...x, ...singleCustData } : x));
            } else {
                // Add new item
                const lastIndex = items.length > 0 ? items[items.length - 1].index || 0 : 0;
                const newIndex = lastIndex + 1;

                const newItem = { ...singleCustData, index: newIndex };
                next = [...items, newItem];
                // or to add at top: next = [newItem, ...items];
            }

            return {
                ...prev,
                items: next,
                totalSize: next.length,
                added: next.length > 0,
            };
        });

        setSingleCustData({
            ...SINGLE_CUST_FORM,
            choice: CustRelationChoice.RELATIVE,
        });
        setIsEdit(false);
        setSpocAdded(true);
    };


    const handleEditRow = (r) => {
        setIsEdit(true);
        setSingleCustData(prev => ({ ...prev, ...r }));
    }

    const handleDeleteRow = (r) => {
        if (paymentInitiated) {
            setSnpComponent(prev => ({
                ...prev,
                snackBarMsg: "Paid bill cannot be deleted",
                snackBarOpen: true,
            }));
            return;
        }
        if (disabledOperation) {
            setSnpComponent(prev => ({
                ...prev,
                snackBarOpen: true,
                snackBarMsg: "Cancelled bill cannot be deleted",
            }));
            return;
        }
        if (r.choice === CustRelationChoice.SPOC) {
            setSnpComponent(prev => ({
                ...prev,
                snackBarMsg: "Spoc cannot be deleted. Please Edit the details",
                snackBarOpen: true,
            }));
            return;
        }
        if (isEmpty(billId)) {
            custDelSnippet(r.mobNo);
        } else {
            deletCustomerData(billId, r);
        }
        setIsEdit(false);

    }

    const handleDeleteCurrent = () => {
        handleDeleteRow(singleCustData);
    }

    return (
        <Grid container spacing={1}>
            <Grid item xs={12}>
                <Paper
                    elevation={4}
                    sx={{ borderRadius: 2, p: 2, backgroundColor: "#f8f4e5" }}
                >
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                            <Button
                                variant="outlined"
                                color="error"
                                onPointerDown={() => setShowForm(!showForm)}
                            >
                                {showForm ? "Hide Form" : "Show Form"}
                            </Button>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                variant="filled"
                                size="small"
                                disabled={disabledOperation}
                                label="Total Estimated Person"
                                onChange={(e) => setKeyMapInformation(prev => {
                                    const newMap = new Map(prev);
                                    newMap.set(ESTIMATED_ADULTS, e.target.value);
                                    return newMap;
                                })}
                                value={keyMapInformation.get(ESTIMATED_ADULTS) ?? ""}
                            />
                        </Grid>
                        {
                            showForm &&
                            <Grid item xs={12}>
                                <Grid container spacing={2}>
                                    <Grid item md={12} sm={12} xs={12}>
                                        <FormLabel component="legend">Select Customer Selection</FormLabel>
                                        <RadioGroup
                                            row
                                            aria-label="choice"
                                            name="choice"
                                            value={singleCustData.choice ?? CustRelationChoice.SPOC}
                                            onChange={(e) =>
                                                setSingleCustData((s) => ({
                                                    ...s,
                                                    choice: Number(e.target.value),
                                                    relation:
                                                        Number(e.target.value) === CustRelationChoice.SPOC
                                                            ? "Spoc"
                                                            : "",
                                                }))
                                            }
                                        >
                                            <FormControlLabel
                                                disabled={isEdit || spocAdded}
                                                value={CustRelationChoice.SPOC}
                                                control={<Radio />}
                                                style={{ marginLeft: "0", marginRight: "0" }}
                                                label="Spoc"
                                            />
                                            <FormControlLabel
                                                disabled={isEdit || !spocAdded}
                                                value={CustRelationChoice.RELATIVE}
                                                control={<Radio />}
                                                style={{ marginLeft: "0", marginRight: "0" }}
                                                label="Family/Friends"
                                            />
                                        </RadioGroup>
                                    </Grid>

                                    {/* Mobile Number (controlled; fetch on blur to avoid spamming) */}
                                    <Grid item xs={12} md={6} sm={12}>
                                        <TextField
                                            required={singleCustData.choice === CustRelationChoice.SPOC}
                                            id="mobNo"
                                            label="Enter Mob No"
                                            name="mobNo"
                                            variant="filled"
                                            max="10"
                                            size="small"
                                            value={singleCustData.mobNo ?? ""}
                                            onChange={(e) =>
                                                handleGetCustomerByMobile(e.target.value, spocAdded)
                                            }
                                            inputProps={{
                                                maxLength: 10,
                                                inputMode: "numeric",
                                                pattern: "[0-9]*"
                                            }}
                                            fullWidth
                                        />
                                    </Grid>

                                    <Grid item xs={12} md={6} sm={12}>
                                        <TextField
                                            required
                                            id="custName"
                                            label="Enter Customer Name"
                                            name="custName"
                                            variant="filled"
                                            size="small"
                                            value={singleCustData.custName ?? ""}
                                            onChange={(e) =>
                                                setSingleCustData((s) => ({
                                                    ...s,
                                                    custName: e.target.value,
                                                }))
                                            }
                                            fullWidth
                                        />
                                    </Grid>

                                    <Grid item md={6} sm={12} xs={12}>
                                        <FormLabel component="legend">Select Gender</FormLabel>
                                        <RadioGroup
                                            row
                                            aria-label="sex"
                                            name="sex"
                                            value={singleCustData.sex ?? CustSex.Male}
                                            onChange={(e) =>
                                                setSingleCustData((s) => ({ ...s, sex: e.target.value }))
                                            }
                                        >
                                            <FormControlLabel
                                                value={CustSex.Male}
                                                control={<Radio />}
                                                style={{ marginLeft: "0", marginRight: "0" }}
                                                label={CustSex.Male}
                                            />
                                            <FormControlLabel
                                                value={CustSex.Female}
                                                control={<Radio />}
                                                style={{ marginLeft: "0", marginRight: "0" }}
                                                label={CustSex.Female}
                                            />
                                            <FormControlLabel
                                                value={CustSex.Other}
                                                control={<Radio />}
                                                style={{ marginLeft: "0", marginRight: "0" }}
                                                label={CustSex.Other}
                                            />
                                        </RadioGroup>
                                    </Grid>

                                    {singleCustData.choice === CustRelationChoice.SPOC && (
                                        <Grid item xs={12} md={6} sm={12}>
                                            <TextField
                                                id="emailId"
                                                label="Enter Email"
                                                name="emailId"
                                                variant="filled"
                                                size="small"
                                                value={singleCustData.emailId ?? ""}
                                                onChange={(e) =>
                                                    setSingleCustData((s) => ({
                                                        ...s,
                                                        emailId: e.target.value,
                                                    }))
                                                }
                                                fullWidth
                                            />
                                        </Grid>
                                    )}

                                    <Grid item xs={12} md={6} sm={12}>
                                        <TextField
                                            id="aadharId"
                                            label="Enter Aadhar"
                                            name="aadharId"
                                            variant="filled"
                                            size="small"
                                            value={singleCustData.aadharId ?? ""}
                                            onChange={(e) =>
                                                setSingleCustData((s) => ({
                                                    ...s,
                                                    aadharId: e.target.value,
                                                }))
                                            }
                                            fullWidth
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={6} sm={12}>
                                        <TextField
                                            id="drivingLicense"
                                            label="Enter Driving License"
                                            name="drivingLicense"
                                            variant="filled"
                                            size="small"
                                            value={singleCustData.drivingLicense}
                                            onChange={(e) => setSingleCustData({
                                                ...singleCustData, drivingLicense: e.target.value,
                                            })}
                                            fullWidth
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={6} sm={12}>
                                        <TextField
                                            id="panNo"
                                            label="Enter Pan No"
                                            name="panNo"
                                            variant="filled"
                                            size="small"
                                            value={singleCustData.panNo}
                                            onChange={(e) => setSingleCustData({
                                                ...singleCustData, panNo: e.target.value,
                                            })}
                                            fullWidth
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={6} sm={12}>
                                        <TextField
                                            id="voterId"
                                            label="Enter Voter Id"
                                            name="voterId"
                                            variant="filled"
                                            size="small"
                                            value={singleCustData.voterId}
                                            onChange={(e) => setSingleCustData({
                                                ...singleCustData, voterId: e.target.value,
                                            })}
                                            fullWidth
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={6} sm={12}>
                                        <TextField
                                            id="passport"
                                            label="Enter Passport"
                                            name="passport"
                                            variant="filled"
                                            size="small"
                                            value={singleCustData.passport}
                                            onChange={(e) => setSingleCustData({
                                                ...singleCustData, passport: e.target.value,
                                            })}
                                            fullWidth
                                        />
                                    </Grid>

                                    {singleCustData.choice === CustRelationChoice.SPOC && (
                                        <Grid item xs={12} md={6} sm={12}>
                                            <TextField
                                                id="custCompanyName"
                                                label="Enter Company Name"
                                                name="custCompanyName"
                                                variant="filled"
                                                size="small"
                                                value={singleCustData.custCompanyName ?? ""}
                                                onChange={(e) =>
                                                    setSingleCustData((s) => ({
                                                        ...s,
                                                        custCompanyName: e.target.value,
                                                    }))
                                                }
                                                fullWidth
                                            />
                                        </Grid>
                                    )}

                                    {singleCustData.choice === CustRelationChoice.SPOC && (
                                        <Grid item xs={12} md={6} sm={12}>
                                            <TextField
                                                id="custGstDetails"
                                                label="Enter GST Details"
                                                name="custGstDetails"
                                                variant="filled"
                                                size="small"
                                                value={singleCustData.custGstDetails ?? ""}
                                                onChange={(e) =>
                                                    setSingleCustData((s) => ({
                                                        ...s,
                                                        custGstDetails: e.target.value,
                                                    }))
                                                }
                                                fullWidth
                                            />
                                        </Grid>
                                    )}

                                    {singleCustData.choice === CustRelationChoice.RELATIVE && (
                                        <Grid item xs={12} md={6} sm={12}>
                                            <TextField
                                                id="relation"
                                                label="Enter Relation"
                                                name="relation"
                                                variant="filled"
                                                size="small"
                                                value={singleCustData.relation ?? ""}
                                                onChange={(e) =>
                                                    setSingleCustData((s) => ({
                                                        ...s,
                                                        relation: e.target.value,
                                                    }))
                                                }
                                                fullWidth
                                            />
                                        </Grid>
                                    )}

                                    {singleCustData.choice === CustRelationChoice.SPOC && (
                                        <Grid item xs={12} md={6} sm={12}>
                                            <TextField
                                                type="date"
                                                id="custDob"
                                                label="Enter Dob"
                                                name="custDob"
                                                variant="filled"
                                                size="small"
                                                value={
                                                    singleCustData.custDob
                                                        ? formatDateInReact(singleCustData.custDob)
                                                        : ""
                                                }
                                                onChange={(e) =>
                                                    setSingleCustData((s) => ({
                                                        ...s,
                                                        custDob: formatDateInSnp(e.target.value),
                                                    }))
                                                }
                                                fullWidth
                                                InputLabelProps={{
                                                    shrink: true,
                                                }}
                                                InputProps={{
                                                    inputProps: {
                                                        min: "1913-01-01",
                                                        max: formatDateInReact(todayFormattedDate),
                                                    },
                                                }}
                                            />
                                        </Grid>
                                    )}

                                    {singleCustData.choice === CustRelationChoice.SPOC && (
                                        <Grid item xs={12} md={6} sm={12}>
                                            <TextField
                                                id="custProfession"
                                                label="Enter Customer Profession"
                                                name="custProfession"
                                                variant="filled"
                                                size="small"
                                                value={singleCustData.custProfession ?? ""}
                                                onChange={(e) =>
                                                    setSingleCustData((s) => ({
                                                        ...s,
                                                        custProfession: e.target.value,
                                                    }))
                                                }
                                                fullWidth
                                            />
                                        </Grid>
                                    )}

                                    {singleCustData.choice === CustRelationChoice.SPOC && (
                                        <Grid item xs={12} md={6} sm={12}>
                                            <TextField
                                                id="custFatherName"
                                                label="Enter Fathers Name"
                                                name="custFatherName"
                                                variant="filled"
                                                size="small"
                                                value={singleCustData.custFatherName ?? ""}
                                                onChange={(e) =>
                                                    setSingleCustData((s) => ({
                                                        ...s,
                                                        custFatherName: e.target.value,
                                                    }))
                                                }
                                                fullWidth
                                            />
                                        </Grid>
                                    )}

                                    {singleCustData.choice === CustRelationChoice.SPOC && (
                                        <Grid item xs={12} md={6} sm={12}>
                                            <TextField
                                                required
                                                id="address"
                                                label="Enter Address"
                                                name="address"
                                                multiline
                                                rows={3}
                                                variant="filled"
                                                size="small"
                                                value={singleCustData.address ?? ""}
                                                onChange={(e) =>
                                                    setSingleCustData((s) => ({
                                                        ...s,
                                                        address: e.target.value,
                                                    }))
                                                }
                                                fullWidth
                                            />
                                        </Grid>
                                    )}
                                    {
                                        !isEmpty(singleCustData.mobNo) && <Grid item xs={12}>
                                            <DocumentManager
                                                token={apiToken}
                                                shopHash={shopHash}
                                                CustMob={singleCustData.mobNo}
                                            />
                                        </Grid>
                                    }

                                </Grid>
                            </Grid>
                        }
                    </Grid>
                </Paper>
            </Grid>
            {
                showForm &&
                <Grid item xs={12} md={4}>
                    <Stack direction="row" spacing={1} sx={{ height: "100%" }} alignItems="center" justifyContent="center">
                        <Button variant="contained" disabled={disabledOperation} onPointerDown={() => handleAddOrUpdate()}>
                            {isEdit ? "Update" : "Add"}
                        </Button>
                        {
                            isEdit &&
                            <Button
                                variant="outlined"
                                color="error"
                                disabled={disabledOperation}
                                onPointerDown={() => handleDeleteCurrent()}
                            >
                                Delete
                            </Button>
                        }
                        <Button variant="text" disabled={disabledOperation} onPointerDown={() => setSingleCustData({
                            ...SINGLE_CUST_FORM,
                            choice: spocAdded ? CustRelationChoice.RELATIVE : CustRelationChoice.SPOC,
                        })}>Reset</Button>
                    </Stack>
                </Grid>
            }
            <Grid item xs={12}>
                <TableContainer component={Paper} elevation={3} sx={{
                    maxHeight: 300,
                    overflowY: "auto",
                    boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.2)",
                    border: "none",
                    borderRadius: 2,
                }}
                >
                    <Table aria-label="extra-orders-table">
                        <TableHead>
                            <TableRow sx={{
                                backgroundColor: "#01333e",
                                "& .MuiTableCell-root": {
                                    color: "#fff",
                                    fontWeight: "bold",
                                    fontSize: "0.8rem",
                                    padding: "6px 8px",
                                    borderBottom: "none",
                                },
                            }}>
                                <TableCell align="center"></TableCell>
                                <TableCell align="center">Customer Name</TableCell>
                                <TableCell align="center">Mob</TableCell>
                                <TableCell align="center">Relation</TableCell>
                                <TableCell align="center">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {customerInformation?.items.length === 0 ? (
                                <TableRow>
                                    <TableCell align="center" colSpan={7}>
                                        <Typography variant="body2" color="text.secondary">
                                            No items yet. Add some above.
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                customerInformation?.items.map((r) => (
                                    <TableRow key={`${r.id}::${r.mobNo}`}>
                                        <TableCell align="center">{r.choice === CustRelationChoice.SPOC ? "Primary" : "Relatives"}</TableCell>
                                        <TableCell align="center">{r.custName}</TableCell>
                                        <TableCell align="center">{r.mobNo}</TableCell>
                                        <TableCell align="center">{r.relation}</TableCell>
                                        <TableCell align="center">
                                            <Stack direction="row" spacing={1} justifyContent="center">
                                                <IconButton
                                                    size="small"
                                                    aria-label="edit"
                                                    onPointerDown={() => handleEditRow(r)}
                                                >
                                                    <EditIcon fontSize="small" />
                                                </IconButton>
                                                <IconButton
                                                    size="small"
                                                    aria-label="delete"
                                                    onPointerDown={() => handleDeleteRow(r)}
                                                >
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </Stack>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Grid>
        </Grid>
    );
};

export { CustomerDetailsForm };
